import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Send, Copy, ExternalLink, Zap, Video, FileText, User, RefreshCw } from 'lucide-react';
import { getAuthHeaders } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ITEM_TEMPLATES = [
  { type: 'endorsement_post', label: 'Creator endorsement post', department: 'Media', price: 1500, billing: 'one_time' },
  { type: 'video_testimonial', label: 'Executive video testimonial', department: 'Media', price: 2000, billing: 'one_time' },
  { type: 'advisory_call', label: 'Executive advisory call', department: 'Media', price: 500, billing: 'one_time' },
  { type: 'amplify', label: 'Amplify — LinkedIn Ads', department: 'Marketing', price: 5750, billing: 'one_time', ad_spend_usd: 5000, channel: 'linkedin', description: '$5,000 ad spend + $500 base + 15% mgmt' },
  { type: 'recurring_amplify', label: 'Amplify — Monthly Meta Ads', department: 'Marketing', price: 6250, billing: 'monthly', ad_spend_usd: 5000, channel: 'meta', description: '$5,000/mo ad spend + $500 base + 15% mgmt' },
  { type: 'shoot_day', label: 'On-site shoot day', department: 'Marketing', price: 2500, billing: 'one_time' },
  { type: 'clip_retainer_pro', label: 'Clip retainer (Pro)', department: 'Marketing', price: 3500, billing: 'monthly' },
  { type: 'blog_post', label: 'Blog post', department: 'Marketing', price: 600, billing: 'one_time' },
  { type: 'white_paper', label: 'White paper', department: 'Marketing', price: 2500, billing: 'one_time' },
  { type: 'social_copy_pack', label: 'Social copy pack (10 posts)', department: 'Marketing', price: 750, billing: 'one_time' },
  { type: 'content_retainer', label: 'Content retainer (monthly)', department: 'Marketing', price: 3500, billing: 'monthly' },
  { type: 'custom_marketing', label: 'Custom line item', department: 'Marketing', price: 0, billing: 'one_time' },
];

const CATEGORY_ICON = { Media: User, Marketing: Zap, Ops: FileText };

function ProposalBuilder({ brands, onCreated, onCancel }) {
  const [brandId, setBrandId] = useState(brands[0]?.id || '');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  const addItem = (tpl) => {
    setItems([...items, { ...tpl, key: `tmp_${Date.now()}_${Math.random()}`, quantity: 1, notes: '' }]);
  };
  const updateItem = (i, patch) => setItems(items.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const oneTime = items.filter(i => i.billing === 'one_time').reduce((s, i) => s + (i.price || 0) * (i.quantity || 1), 0);
  const monthly = items.filter(i => i.billing === 'monthly').reduce((s, i) => s + (i.price || 0) * (i.quantity || 1), 0);

  const submit = async () => {
    if (!brandId || !title || items.length === 0) { toast.error('Pick a brand, title, and at least one line item.'); return; }
    setSaving(true);
    try {
      const payload = {
        brand_id: brandId, title, description,
        line_items: items.map(i => ({
          type: i.type, label: i.label, description: i.description || '',
          price_usd: parseFloat(i.price) || 0, billing: i.billing, quantity: i.quantity,
          channel: i.channel, ad_spend_usd: i.ad_spend_usd, notes: i.notes,
        })),
      };
      const r = await axios.post(`${API}/admin/proposals`, payload, { headers: getAuthHeaders() });
      toast.success('Proposal created — send it from the list.');
      onCreated(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-[#111827] border border-white/10 p-5 space-y-4" data-testid="proposal-builder">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-bold text-white">Build a proposal</h3>
        <button onClick={onCancel} className="text-xs text-[#9CA3AF] hover:text-white">Cancel</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] font-mono uppercase text-[#6B7280] block mb-1">Brand *</label>
          <select value={brandId} onChange={e => setBrandId(e.target.value)} data-testid="proposal-brand-select"
            className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm">
            <option value="">— pick a brand —</option>
            {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase text-[#6B7280] block mb-1">Title *</label>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Q1 B2B launch bundle"
            data-testid="proposal-title" className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm" />
        </div>
      </div>
      <div>
        <label className="text-[10px] font-mono uppercase text-[#6B7280] block mb-1">Description / scope narrative</label>
        <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3}
          data-testid="proposal-desc" className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm" />
      </div>
      <div>
        <p className="text-[10px] font-mono uppercase text-[#6B7280] mb-2">Add line items — click to add</p>
        <div className="flex flex-wrap gap-1.5 mb-4">
          {ITEM_TEMPLATES.map((tpl, i) => (
            <button key={i} onClick={() => addItem(tpl)} data-testid={`add-item-${tpl.type}`}
              className="text-[10px] px-2 py-1 border border-white/10 text-[#9CA3AF] hover:border-[#FF4343] hover:text-[#FF4343]">
              + {tpl.label}
            </button>
          ))}
        </div>
        <div className="space-y-2">
          {items.map((it, i) => {
            const Icon = CATEGORY_ICON[it.department] || FileText;
            return (
              <div key={it.key} className="bg-[#090E33] border border-white/10 p-3 flex items-start gap-2" data-testid={`li-row-${i}`}>
                <Icon className="w-4 h-4 text-[#FF4343] mt-1 flex-shrink-0" />
                <div className="flex-1 grid grid-cols-1 md:grid-cols-6 gap-2">
                  <input value={it.label} onChange={e => updateItem(i, { label: e.target.value })} placeholder="Label"
                    className="md:col-span-2 px-2 py-1.5 bg-[#111827] border border-white/10 text-white text-xs" />
                  <input value={it.description || ''} onChange={e => updateItem(i, { description: e.target.value })} placeholder="Description"
                    className="md:col-span-2 px-2 py-1.5 bg-[#111827] border border-white/10 text-white text-xs" />
                  <input type="number" value={it.price} onChange={e => updateItem(i, { price: e.target.value })} placeholder="Price"
                    className="px-2 py-1.5 bg-[#111827] border border-white/10 text-white text-xs font-mono" />
                  <select value={it.billing} onChange={e => updateItem(i, { billing: e.target.value })}
                    className="px-2 py-1.5 bg-[#111827] border border-white/10 text-white text-xs">
                    <option value="one_time">One-time</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                <button onClick={() => removeItem(i)} data-testid={`li-remove-${i}`} className="text-[#FF4343] hover:text-white">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      </div>
      <div className="border-t border-white/5 pt-4 flex items-center justify-between">
        <div className="font-mono text-sm space-y-1">
          <div className="text-[#9CA3AF]">One-time: <span className="text-white">${oneTime.toLocaleString()}</span></div>
          <div className="text-[#9CA3AF]">Monthly: <span className="text-white">${monthly.toLocaleString()}</span></div>
        </div>
        <button onClick={submit} disabled={saving} data-testid="save-proposal"
          className="border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] px-5 py-2.5 text-sm font-semibold hover:bg-[#E03030] disabled:opacity-50">
          {saving ? 'Saving…' : 'Save draft'}
        </button>
      </div>
    </div>
  );
}

const STATUS_TINT = {
  draft: 'text-[#9CA3AF] border-white/10 bg-white/5',
  sent: 'text-yellow-400 border-yellow-500/30 bg-yellow-500/10',
  signed: 'text-[#FF4343] border-[#FF4343]/30 bg-[#FF4343]/10',
  paid: 'text-[#10B981] border-[#10B981]/30 bg-[#10B981]/10',
  executing: 'text-[#10B981] border-[#10B981]/30 bg-[#10B981]/10',
  completed: 'text-[#10B981] border-[#10B981]/30 bg-[#10B981]/10',
  cancelled: 'text-[#6B7280] border-white/10 bg-white/5',
};

export default function AdminProposals() {
  const [brands, setBrands] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBuilder, setShowBuilder] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [b, p] = await Promise.all([
        axios.get(`${API}/admin/signups`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/proposals`, { headers: getAuthHeaders() }),
      ]);
      setBrands(b.data.brands || []);
      setProposals(p.data.proposals || []);
    } catch { toast.error('Load failed'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const sendProposal = async (id) => {
    try {
      const r = await axios.post(`${API}/admin/proposals/${id}/send`, { portal_url: window.location.origin }, { headers: getAuthHeaders() });
      if (r.data.delivery_status === 'sent') toast.success('Emailed to brand'); else toast.info('Link generated (email disabled)');
      navigator.clipboard.writeText(r.data.sign_url);
      toast.success('Sign link copied to clipboard');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
  };

  const updateTask = async (propId, taskId, status) => {
    try {
      await axios.put(`${API}/admin/proposals/${propId}/task/${taskId}`, { status }, { headers: getAuthHeaders() });
      load();
    } catch (e) { toast.error('Task update failed'); }
  };

  return (
    <div className="space-y-5" data-testid="admin-proposals">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold text-white">Proposals</h2>
          <p className="text-xs text-[#9CA3AF]">Build multi-layer SOWs, send for e-signature, collect payment, route tasks to your team.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="text-xs px-3 py-2 border border-white/20 text-white hover:bg-white/5 flex items-center gap-1.5">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <button onClick={() => setShowBuilder(true)} data-testid="new-proposal-btn"
            className="text-xs px-4 py-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] font-semibold flex items-center gap-1.5 hover:bg-[#E03030]">
            <Plus className="w-3.5 h-3.5" /> New proposal
          </button>
        </div>
      </div>

      {showBuilder && (
        <ProposalBuilder brands={brands} onCreated={() => { setShowBuilder(false); load(); }} onCancel={() => setShowBuilder(false)} />
      )}

      {loading ? <p className="text-sm text-[#9CA3AF]">Loading…</p> : proposals.length === 0 ? (
        <div className="text-center py-10 bg-[#111827] border border-white/10">
          <p className="text-sm text-[#9CA3AF]">No proposals yet. Build your first SOW above.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {proposals.map(p => (
            <div key={p.id} className="bg-[#111827] border border-white/10" data-testid={`prop-row-${p.id}`}>
              <div className="p-4 flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-sm font-semibold text-white">{p.title}</span>
                    <span className={`text-[10px] font-mono uppercase border px-1.5 py-0.5 ${STATUS_TINT[p.status]}`}>{p.status}</span>
                  </div>
                  <p className="text-xs text-[#9CA3AF]">{p.brand_name} · {p.line_items.length} line item{p.line_items.length !== 1 ? 's' : ''}</p>
                  <p className="text-[10px] text-[#6B7280] font-mono">${p.one_time_total_usd.toLocaleString()} one-time + ${p.monthly_total_usd.toLocaleString()}/mo · {new Date(p.created_at).toLocaleDateString()}</p>
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  {['draft','sent'].includes(p.status) && (
                    <button onClick={() => sendProposal(p.id)} data-testid={`send-${p.id}`}
                      className="text-[11px] px-3 py-1.5 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] font-semibold hover:bg-[#E03030] flex items-center gap-1">
                      <Send className="w-3 h-3" /> {p.status === 'draft' ? 'Send' : 'Re-send'}
                    </button>
                  )}
                  <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/proposal?token=${p.sign_token}`); toast.success('Link copied'); }}
                    className="text-[11px] px-3 py-1.5 border border-white/20 text-white hover:bg-white/5 flex items-center gap-1">
                    <Copy className="w-3 h-3" /> Copy link
                  </button>
                  <a href={`${window.location.origin}/proposal?token=${p.sign_token}`} target="_blank" rel="noopener noreferrer"
                    className="text-[11px] px-3 py-1.5 border border-white/20 text-white hover:bg-white/5 flex items-center gap-1">
                    Preview <ExternalLink className="w-3 h-3" />
                  </a>
                  <button onClick={() => setExpanded(expanded === p.id ? null : p.id)} data-testid={`expand-${p.id}`}
                    className="text-[11px] px-3 py-1.5 border border-white/20 text-white hover:bg-white/5">
                    {expanded === p.id ? 'Hide' : 'Tasks'}
                  </button>
                </div>
              </div>
              {expanded === p.id && (
                <div className="border-t border-white/10 p-4 bg-[#090E33]/50" data-testid={`tasks-${p.id}`}>
                  <p className="text-[10px] font-mono uppercase text-[#6B7280] mb-2">Auto-generated task list (department-routed)</p>
                  {p.status === 'draft' || p.status === 'sent' ? (
                    <p className="text-xs text-[#9CA3AF] italic">Tasks unlock once the brand signs + pays.</p>
                  ) : (
                    <div className="space-y-1">
                      {p.tasks.map(t => (
                        <div key={t.id} className="flex items-center gap-2 text-xs bg-white/5 p-2">
                          <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 ${t.department === 'Media' ? 'bg-[#FF4343]/10 text-[#FF4343]' : 'bg-[#10B981]/10 text-[#10B981]'}`}>{t.department}</span>
                          <span className="flex-1 text-white">{t.label}</span>
                          <select value={t.status} onChange={e => updateTask(p.id, t.id, e.target.value)}
                            className="text-[10px] bg-[#090E33] border border-white/10 text-white px-2 py-0.5">
                            <option value="queued">queued</option>
                            <option value="in_progress">in progress</option>
                            <option value="blocked">blocked</option>
                            <option value="done">done</option>
                          </select>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
